/**
 * React Password Reset Confirm Page Component
 *
 * Branded password reset confirmation page - allows user to set a new password
 * using the token from the reset email.
 *
 * Features:
 * - Matches the styling and layout of the LoginPage
 * - Password validation and confirmation
 * - CSRF protection
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import './LoginPage.css';

interface PasswordResetFormData {
  new_password1: string;
  new_password2: string;
}

interface PasswordResetError {
  field?: string;
  message: string;
}

const PasswordResetConfirmPage: React.FC = () => {
  const { uidb64, token } = useParams<{ uidb64: string; token: string }>();

  // Get config from window if available (Django-served) or use defaults (React route)
  const config = (window as any).passwordResetContext || {};
  const singleUser = config.singleUser || false;
  const logo = config.logo || '/static/imgs/tecdoc-logo.png.webp';
  const version = config.version || '';
  const deployedEnv = config.deployedEnv || '';
  
  const [formData, setFormData] = useState<PasswordResetFormData>({
    new_password1: '',
    new_password2: ''
  });

  const [errors, setErrors] = useState<PasswordResetError[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [linkValid, setLinkValid] = useState(true);
  const username = config.username || '';

  const password1InputRef = useRef<HTMLInputElement>(null);

  // Focus first password input on mount
  useEffect(() => {
    if (password1InputRef.current && linkValid) {
      password1InputRef.current.focus();
    }
  }, [linkValid]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors([]);

    // Client-side validation
    if (formData.new_password1 !== formData.new_password2) {
      setErrors([{ field: 'new_password2', message: 'Passwords do not match' }]);
      return;
    }

    if (formData.new_password1.length < 8) {
      setErrors([{ field: 'new_password1', message: 'Password must be at least 8 characters' }]);
      return;
    }

    setIsLoading(true);

    try {
      // Submit to API endpoint
      const response = await fetch('/auth/api/reset-password-confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken()
        },
        credentials: 'include',
        body: JSON.stringify({
          uidb64,
          token,
          new_password: formData.new_password1
        })
      });

      const data = await response.json();

      if (data.success) {
        setIsSuccess(true);
      } else {
        setErrors([{ message: data.error || 'Password reset failed. Please try again.' }]);
      }
    } catch (error) {
      setErrors([{ message: 'An error occurred. Please try again.' }]);
      console.error('Password reset error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCsrfToken = (): string => {
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrftoken='));
    return cookie ? cookie.split('=')[1] : '';
  };

  const fieldErrors = (fieldName: string) => {
    return errors.filter(e => e.field === fieldName);
  };

  const nonFieldErrors = errors.filter(e => !e.field);

  // Show success message
  if (isSuccess) {
    return (
      <div id="main">
        <div id="values" style={{ textAlign: 'center', top: '100px' }}>
          {singleUser ? (
            <img src="/static/imgs/TECDoc_Scribe_logo.png.webp" alt="TECDoc Scribe" />
          ) : (
            <img src="/static/imgs/tecdoc-logo.png.webp" alt="TECDoc" />
          )}
        </div>

        <div id="auth-column">
          <div id="auth-logo-container">
            {!singleUser && logo && (
              <img id="auth-logo" src={logo} alt="Facility Logo" />
            )}
          </div>

          <div id="auth-form-container">
            <h1>PASSWORD CHANGED</h1>

            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p style={{ marginBottom: '20px', fontSize: '16px', color: '#28a745' }}>
                ✅ Your password has been changed successfully.
              </p>
              <p style={{ marginBottom: '30px', fontSize: '14px', color: '#666' }}>
                You can now sign in with your new password.
              </p>
            </div>

            <div className="submit-row">
              <Link to="/login" className="auth-button" style={{ textDecoration: 'none', display: 'inline-block', textAlign: 'center' }}>
                SIGN IN
              </Link>
            </div>
          </div>

          <span className="version-number">
            {version} {deployedEnv}
          </span>
        </div>
      </div>
    );
  }

  // Show invalid link message
  if (!linkValid) {
    return (
      <div id="main">
        <div id="values" style={{ textAlign: 'center', top: '100px' }}>
          {singleUser ? (
            <img src="/static/imgs/TECDoc_Scribe_logo.png.webp" alt="TECDoc Scribe" />
          ) : (
            <img src="/static/imgs/tecdoc-logo.png.webp" alt="TECDoc" />
          )}
        </div>

        <div id="auth-column">
          <div id="auth-logo-container">
            {!singleUser && logo && (
              <img id="auth-logo" src={logo} alt="Facility Logo" />
            )}
          </div>

          <div id="auth-form-container">
            <h1>INVALID LINK</h1>

            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p style={{ marginBottom: '20px', fontSize: '14px', color: '#666' }}>
                The password reset link was invalid, possibly because it has already been used.
              </p>
              <p style={{ marginBottom: '30px', fontSize: '14px', color: '#666' }}>
                Please request a new password reset.
              </p>
            </div>

            <div className="submit-row">
              <Link to="/forgot-password" className="auth-button" style={{ textDecoration: 'none', display: 'inline-block', textAlign: 'center' }}>
                REQUEST NEW RESET
              </Link>
            </div>
          </div>

          <span className="version-number">
            {version} {deployedEnv}
          </span>
        </div>
      </div>
    );
  }

  // Show password reset form
  return (
    <div id="main">
      <div id="values" style={{ textAlign: 'center', top: '100px' }}>
        {singleUser ? (
          <img src="/static/imgs/TECDoc_Scribe_logo.png.webp" alt="TECDoc Scribe" />
        ) : (
          <img src="/static/imgs/tecdoc-logo.png.webp" alt="TECDoc" />
        )}
      </div>

      <div id="auth-column">
        <div id="auth-logo-container">
          {!singleUser && logo && (
            <img id="auth-logo" src={logo} alt="Facility Logo" />
          )}
        </div>

        <div id="auth-form-container">
          <h1>CHANGE PASSWORD</h1>

          {username && (
            <p style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
              Howdy {username},
            </p>
          )}

          <p className="instructions" style={{ marginBottom: '20px' }}>
            Enter your new password:
          </p>

          {errors.length > 0 && fieldErrors('new_password1').length === 0 && fieldErrors('new_password2').length === 0 && (
            <p className="errornote">Please correct the errors below.</p>
          )}

          {nonFieldErrors.map((error, index) => (
            <p key={index} className="errornote">
              {error.message}
            </p>
          ))}

          <form onSubmit={handleSubmit} action="" method="post">
            <input type="hidden" name="csrfmiddlewaretoken" value={getCsrfToken()} />
            
            <div className="field spaced">
              <label htmlFor="id_new_password1" className="label">New Password</label>
              <input
                type="password"
                id="id_new_password1"
                name="new_password1"
                value={formData.new_password1}
                onChange={handleInputChange}
                ref={password1InputRef}
                disabled={isLoading}
                required
                minLength={8}
              />
              {fieldErrors('new_password1').map((error, index) => (
                <p key={index} className="errornote">{error.message}</p>
              ))}
            </div>

            <div className="field spaced">
              <label htmlFor="id_new_password2" className="label">Confirm Password</label>
              <input
                type="password"
                id="id_new_password2"
                name="new_password2"
                value={formData.new_password2}
                onChange={handleInputChange}
                disabled={isLoading}
                required
                minLength={8}
              />
              {fieldErrors('new_password2').map((error, index) => (
                <p key={index} className="errornote">{error.message}</p>
              ))}
            </div>

            <div className="submit-row">
              <input
                type="submit"
                value={isLoading ? 'CHANGING...' : 'CHANGE PASSWORD'}
                className="auth-button"
                disabled={isLoading}
              />
            </div>
          </form>
        </div>

        <span className="version-number">
          {version} {deployedEnv}
        </span>
      </div>
    </div>
  );
};

export default PasswordResetConfirmPage;
